import React, { useState, useEffect } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import ProductNav from './components/ProductNav';

// Credit costs
const ANIMATION_OPTIONS = [
  {
    id: '5sec',
    label: '5 Second Animation',
    duration: 5,
    credits: 1,
    desc: 'Short, impactful motion clip.',
  },
  {
    id: '10sec',
    label: '10 Second Animation',
    duration: 10,
    credits: 2,
    desc: 'Extended animated sequence.',
  },
];

export default function AnimatePage() {
  const navigate                    = useNavigate();
  const [searchParams]              = useSearchParams();
  const { user }                    = useAuth();

  const imageId = searchParams.get('image_id') || null;

  const [imageUrl,        setImageUrl]        = useState(null);
  const [imageLoading,    setImageLoading]    = useState(true);
  const [imageError,      setImageError]      = useState(false);
  const [animCredits,     setAnimCredits]     = useState(null);
  const [selected,        setSelected]        = useState('5sec');
  const [generating,      setGenerating]      = useState(false);
  const [animationUrl,    setAnimationUrl]    = useState(null);
  const [error,           setError]           = useState('');
  const [imgLoaded,       setImgLoaded]       = useState(false);

  const selectedOption = ANIMATION_OPTIONS.find(o => o.id === selected);

  useEffect(() => {
    if (!imageId) {
      setImageError(true);
      setImageLoading(false);
      return;
    }
    setImageLoading(true);
    API.get(`/v1/images/job/${imageId}`)
      .then(res => {
        setImageUrl(res.data?.image_url || null);
        if (!res.data?.image_url) setImageError(true);
      })
      .catch(() => setImageError(true))
      .finally(() => setImageLoading(false));
  }, [imageId]);

  useEffect(() => {
    API.get('/v1/animation/credits')
      .then(res => setAnimCredits(res.data?.balance ?? 0))
      .catch(() => setAnimCredits(0));
  }, []);

  async function handleGenerate() {
    if (!imageId || !selectedOption) return;
    if (animCredits !== null && animCredits < selectedOption.credits) {
      setError(`Insufficient credits. This animation costs ${selectedOption.credits} credit${selectedOption.credits > 1 ? 's' : ''}. You have ${animCredits}.`);
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const res = await API.post('/v1/animation/generate', {
        image_id: imageId,
        duration: selectedOption.duration,
      });
      setAnimationUrl(res.data?.animation_url || null);
      setAnimCredits(prev => (prev !== null ? prev - selectedOption.credits : null));
    } catch (err) {
      setError(err.response?.data?.error || 'Animation generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  function handleDownload() {
    if (!animationUrl) return;
    const a = document.createElement('a');
    a.href     = animationUrl;
    a.download = `visinvoke-animation-${imageId?.slice(0, 8) || 'output'}.mp4`;
    a.target   = '_blank';
    a.rel      = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // No image selected guard
  if (!imageId) {
    return (
      <div style={s.page}>
        <ProductNav />
        <div style={s.container}>
          <div style={s.centreCard}>
            <div style={s.centreIcon}>🔍</div>
            <div style={s.centreTitle}>No Image Selected</div>
            <div style={s.centreSub}>Return to your generated image and click "Animate This" to begin.</div>
            <button style={s.btnPrimary} onClick={() => navigate('/workspace')}>View My Images</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <ProductNav />

      <div style={s.container}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <span style={s.crumb} onClick={() => navigate('/workspace')}>My Images</span>
          <span style={s.crumbSep}>{'>'}</span>
          <span style={s.crumbActive}>Animate</span>
        </div>

        {/* Page header */}
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Animate Your Creation</h1>
            <p style={s.pageSub}>Turn your finished image into a short animated sequence.</p>
          </div>
          <div style={s.creditPill}>
            <span style={s.creditPillIcon}>▶</span>
            <span style={s.creditPillLabel}>Animation Credits:</span>
            <span style={s.creditPillVal}>{animCredits === null ? '-' : animCredits}</span>
            <button style={s.buyCreditsLink} onClick={() => navigate('/animation-credits')}>
              Buy Credits
            </button>
          </div>
        </div>

        {/* Messaging strip */}
        <div style={s.messagingStrip}>
          <div style={s.messagingItem}><span style={s.messagingDot} />Optional add-on - not required to use VISINVOKE</div>
          <div style={s.messagingItem}><span style={s.messagingDot} />Does not affect your original image</div>
          <div style={s.messagingItem}><span style={s.messagingDot} />Designed for enhancement, not replacement</div>
        </div>

        {/* Main layout */}
        <div style={s.mainGrid}>

          {/* Left - image preview */}
          <div style={s.leftCol}>
            <div style={s.sectionLabel}>Source Image</div>
            <div style={s.imageBox}>
              {imageLoading && (
                <div style={s.imageLoading}>
                  <div style={s.imageSpinner} />
                  <span style={s.imageLoadingText}>Loading image...</span>
                </div>
              )}
              {imageError && !imageLoading && (
                <div style={s.imageLoading}>
                  <div style={s.imageErrorIcon}>⚠️</div>
                  <span style={s.imageLoadingText}>Image could not be loaded.</span>
                </div>
              )}
              {imageUrl && !imageError && (
                <>
                  <img
                    src={imageUrl}
                    alt="Source for animation"
                    style={{ ...s.sourceImg, display: imgLoaded ? 'block' : 'none' }}
                    onLoad={() => setImgLoaded(true)}
                  />
                  {!imgLoaded && (
                    <div style={s.imageLoading}>
                      <div style={s.imageSpinner} />
                    </div>
                  )}
                </>
              )}
              {imgLoaded && (
                <div style={s.imageLockBadge}>🔒 Locked - source image</div>
              )}
            </div>
          </div>

          {/* Right - options + CTA */}
          <div style={s.rightCol}>

            <div style={s.sectionLabel}>Animation Length</div>
            <div style={s.optionsCol}>
              {ANIMATION_OPTIONS.map(opt => {
                const active = selected === opt.id;
                return (
                  <div
                    key={opt.id}
                    style={{
                      ...s.optionCard,
                      borderColor: active ? '#15803d' : '#e5e7eb',
                      boxShadow: active ? '0 0 0 2px #15803d33' : 'none',
                      background: active ? '#f0fdf4' : '#fff',
                    }}
                    onClick={() => setSelected(opt.id)}
                  >
                    <div style={s.optionLeft}>
                      <div style={{
                        ...s.optionRadio,
                        borderColor: active ? '#15803d' : '#d1d5db',
                        background: active ? '#15803d' : '#fff',
                      }}>
                        {active && <div style={s.optionRadioDot} />}
                      </div>
                      <div>
                        <div style={s.optionTitle}>{opt.label}</div>
                        <div style={s.optionDesc}>{opt.desc}</div>
                      </div>
                    </div>
                    <div style={s.optionRight}>
                      <div style={s.optionCredits}>{opt.credits}</div>
                      <div style={s.optionCreditLabel}>credit{opt.credits > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {animCredits !== null && selectedOption && animCredits < selectedOption.credits && (
              <div style={s.creditWarning}>
                ⚠ You need {selectedOption.credits} credit{selectedOption.credits > 1 ? 's' : ''} for this animation. You have {animCredits}.{' '}
                <span style={s.creditWarningLink} onClick={() => navigate('/animation-credits')}>Purchase credits</span>
              </div>
            )}

            {error && <div style={s.errorBox}>{error}</div>}

            {!animationUrl ? (
              <button
                style={{
                  ...s.btnGenerate,
                  opacity: (generating || imageError || imageLoading) ? 0.6 : 1,
                  cursor: (generating || imageError || imageLoading) ? 'not-allowed' : 'pointer',
                }}
                onClick={handleGenerate}
                disabled={generating || imageError || imageLoading}
              >
                {generating ? 'Generating Animation...' : `Generate Animation - ${selectedOption?.credits} Credit${selectedOption?.credits > 1 ? 's' : ''}`}
              </button>
            ) : (
              <div style={s.resultBox}>
                <div style={s.resultHeader}>
                  <span style={s.resultCheck}>✓</span>
                  <span style={s.resultTitle}>Animation Complete</span>
                </div>
                <video src={animationUrl} controls autoPlay loop style={s.animationVideo} />
                <button style={s.btnDownload} onClick={handleDownload}>⬇ Download Animation</button>
                <button style={s.btnAnother} onClick={() => { setAnimationUrl(null); setError(''); }}>Generate Another</button>
              </div>
            )}

            <div style={s.infoNote}>
              Animations are saved to your history and available for download. Output format: MP4.
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif", color: '#fff' },
  container: { maxWidth: 1100, margin: '0 auto', padding: '32px 40px' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 },
  crumb:      { color: '#a78bfa', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' },
  crumbSep:   { color: 'rgba(255,255,255,0.3)', fontSize: 14 },
  crumbActive:{ color: '#fff', fontSize: 14, fontWeight: 600 },
  pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 20, flexWrap: 'wrap' },
  pageTitle:  { color: '#fff', fontSize: 30, fontWeight: 800, margin: '0 0 6px' },
  pageSub:    { color: 'rgba(255,255,255,0.6)', fontSize: 15, margin: 0 },
  creditPill: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 30, padding: '8px 18px', flexShrink: 0 },
  creditPillIcon:  { fontSize: 14, color: '#4ade80' },
  creditPillLabel: { fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 600 },
  creditPillVal:   { fontSize: 16, fontWeight: 800, color: '#fff' },
  buyCreditsLink:  { ...glassSurface('#0E7A50', '#16a34a', '#4ADE80'), borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginLeft: 4 },
  messagingStrip: { display: 'flex', gap: 28, flexWrap: 'wrap', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 20px', marginBottom: 32 },
  messagingItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.65)' },
  messagingDot:  { width: 6, height: 6, borderRadius: '50%', background: '#4ade80', flexShrink: 0 },
  mainGrid: { display: 'grid', gridTemplateColumns: '1fr 420px', gap: 32, marginBottom: 48, alignItems: 'start' },
  leftCol:  { display: 'flex', flexDirection: 'column', gap: 12 },
  rightCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.45)', marginBottom: 4 },
  imageBox: { background: 'rgba(0,0,0,0.3)', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', minHeight: 360, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  imageLoading: { width: '100%', minHeight: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 },
  imageSpinner: { width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#a78bfa', animation: 'visinvoke-spin 0.8s linear infinite' },
  imageLoadingText: { color: 'rgba(255,255,255,0.4)', fontSize: 14 },
  imageErrorIcon:   { fontSize: 40 },
  sourceImg:        { width: '100%', height: 'auto', display: 'block', borderRadius: 16 },
  imageLockBadge:   { position: 'absolute', bottom: 14, left: 14, background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.7)', fontSize: 11, padding: '4px 12px', borderRadius: 20, backdropFilter: 'blur(4px)' },
  optionsCol:  { display: 'flex', flexDirection: 'column', gap: 10 },
  optionCard:  { background: '#fff', border: '2px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s' },
  optionLeft:  { display: 'flex', alignItems: 'center', gap: 14 },
  optionRadio: { width: 20, height: 20, borderRadius: '50%', border: '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'border-color 0.15s, background 0.15s' },
  optionRadioDot:    { width: 8, height: 8, borderRadius: '50%', background: '#fff' },
  optionTitle:       { fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 2 },
  optionDesc:        { fontSize: 12, color: '#6b7280' },
  optionRight:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 },
  optionCredits:     { fontWeight: 900, fontSize: 26, color: '#15803d', lineHeight: 1 },
  optionCreditLabel: { fontSize: 11, color: '#6b7280', fontWeight: 600 },
  creditWarning:     { background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: '10px 14px', fontSize: 13, lineHeight: 1.5 },
  creditWarningLink: { fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', color: '#9a3412' },
  errorBox:          { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 13, lineHeight: 1.5 },
  btnGenerate: { ...glassSurface('#0E7A50', '#16a34a', '#4ADE80'), borderRadius: 12, padding: '15px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%', transition: 'opacity 0.15s' },
  resultBox:    { background: '#fff', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
  resultHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  resultCheck:  { fontSize: 20, color: '#15803d' },
  resultTitle:  { fontWeight: 800, fontSize: 16, color: '#1a1a1a' },
  animationVideo: { width: '100%', borderRadius: 8, display: 'block' },
  btnDownload: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), borderRadius: 10, padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%' },
  btnAnother:  { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 10, padding: '10px 0', fontWeight: 600, fontSize: 13, cursor: 'pointer', width: '100%' },
  infoNote:    { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 },
  centreCard:  { background: 'rgba(255,255,255,0.97)', borderRadius: 20, padding: '56px 48px', maxWidth: 480, margin: '60px auto 0', boxShadow: '0 16px 48px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, color: '#1a1a1a', textAlign: 'center' },
  centreIcon:  { fontSize: 48 },
  centreTitle: { fontWeight: 800, fontSize: 22, color: '#1a1a1a' },
  centreSub:   { fontSize: 14, color: '#6b7280', lineHeight: 1.6, maxWidth: 340 },
  btnPrimary:  { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), borderRadius: 10, padding: '13px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 },
};

if (typeof document !== 'undefined' && !document.getElementById('visinvoke-spin-kf')) {
  const st = document.createElement('style');
  st.id          = 'visinvoke-spin-kf';
  st.textContent = '@keyframes visinvoke-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(st);
}
